/**
 * Core preloading primitives for aggressive asset preloading.
 * All functions deduplicate requests -- calling with the same URL/key twice
 * returns the same promise without creating a new network request.
 */

// --- Image preloading ---

const imageCache = new Map<string, Promise<void>>();

export function preloadImage(url: string | null | undefined): Promise<void> {
  if (!url) return Promise.resolve();
  if (imageCache.has(url)) return imageCache.get(url)!;

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = url;
  });

  imageCache.set(url, promise);
  return promise;
}

// --- JS chunk preloading ---

const chunkCache = new Map<string, Promise<any>>();

export function preloadChunk(key: string, factory: () => Promise<any>): Promise<any> {
  if (chunkCache.has(key)) return chunkCache.get(key)!;
  const promise = factory().catch((err) => {
    console.warn(`[preloader] Chunk preload failed: ${key}`, err);
    chunkCache.delete(key);
    return undefined;
  });
  chunkCache.set(key, promise);
  return promise;
}

// --- Lottie animation preloading ---

const lottieCache = new Map<string, Promise<any>>();

export const LOTTIE_LOADERS: Record<string, () => Promise<any>> = {
  'personal_plan': () => import('../components/animations/personal_plan.json'),
  'WordSelection_opt': () => import('../components/animations/WordSelection_opt.json'),
  'prog_step_1': () => import('../components/animations/prog_step_1.json'),
  'prog_step_2': () => import('../components/animations/prog_step_2.json'),
  'prog_step_3': () => import('../components/animations/prog_step_3.json'),
  'prog_step_4': () => import('../components/animations/prog_step_4.json'),
  'prog_step_5': () => import('../components/animations/prog_step_5.json'),
  'prog_step_6': () => import('../components/animations/prog_step_6.json'),
  'Time': () => import('../components/animations/Time.json'),
  'Percent_Correct': () => import('../components/animations/Percent_Correct.json'),
  'Badge_opt': () => import('../components/animations/Badge_opt.json'),
  'logo_loader': () => import('../components/animations/logo_loader.json'),
};

export function preloadLottie(name: string): Promise<any> {
  if (lottieCache.has(name)) return lottieCache.get(name)!;

  const loader = LOTTIE_LOADERS[name];
  if (!loader) return Promise.resolve();

  const promise = loader().catch((err) => {
    console.warn(`[preloader] Lottie preload failed: ${name}`, err);
    lottieCache.delete(name);
    return undefined;
  });
  lottieCache.set(name, promise);
  return promise;
}

// --- Idle-time scheduling ---

export function preloadDuringIdle(tasks: Array<() => Promise<any>>): void {
  const schedule = typeof requestIdleCallback !== 'undefined'
    ? (cb: () => void) => requestIdleCallback(cb, { timeout: 2000 })
    : (cb: () => void) => setTimeout(cb, 50);

  let index = 0;

  function runNext() {
    if (index >= tasks.length) return;
    const startIndex = index;
    index += 3;
    schedule(() => {
      const batch = tasks.slice(startIndex, startIndex + 3);
      Promise.all(batch.map(t => t())).then(runNext);
    });
  }

  runNext();
}

// --- Payment Page Warmup ---

let paymentPageWarmed = false;

export function preloadPaymentPage(): void {
  if (paymentPageWarmed) return;
  if (typeof document === 'undefined') return;
  paymentPageWarmed = true;

  const ensureLink = (rel: string, href: string, attrs: Record<string, string> = {}) => {
    if (document.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = rel;
    link.href = href;
    Object.entries(attrs).forEach(([key, value]) => {
      link.setAttribute(key, value);
    });
    document.head.appendChild(link);
  };

  // DNS and connection setup (high value)
  ensureLink('dns-prefetch', 'https://langarena.com');
  ensureLink('preconnect', 'https://langarena.com', { crossorigin: 'anonymous' });

  // Preload Stripe.js (always needed on payment page, ~200KB)
  ensureLink('dns-prefetch', 'https://js.stripe.com');
  ensureLink('preconnect', 'https://js.stripe.com', { crossorigin: 'anonymous' });

  // Document prefetch for next navigation
  ensureLink('prefetch', 'https://langarena.com/payment');

  // Aggressive warmup - fetch immediately
  try {
    fetch('https://langarena.com/payment', {
      mode: 'no-cors',
      cache: 'force-cache',
      keepalive: true
    }).catch(() => {
      // ignore warmup errors
    });
  } catch (e) {
    // ignore
  }
}
